/*
 * bc-kit-scene — the live parametric diagram for the Primitive Kit Builder.
 *
 * The element takes over its inner <svg> on connect: it clears the pre-JS
 * fallback markup, paints a sky + grass backdrop, and owns a single dynamic
 * <g> layer that render() rebuilds from the builder's central state on every
 * change. zoomTo() animates the SVG viewBox toward the left/right tree as the
 * wizard steps change. The contract (render(state, options) / zoomTo(target))
 * is the same one assets/bc-primitive-kit-builder.js already calls, so this is
 * a drop-in replacement for the Phase 1 placeholder.
 *
 * Each interactive component is wrapped in a group tagged data-bc-edit="<role>"
 * so the builder's existing delegated click handler powers click-to-edit (it
 * jumps to the slot's step). The scene reads its colors/shapes from the option
 * config — it never hard-codes a product palette.
 *
 * Coordinate system: a 1600x700 drawing space; the visible viewBox crops to
 * 0 70 1600 550 (canopy tops trimmed, grass band thinned) so the diagram reads
 * shorter. Left tree (static anchor) on the left, right tree (tensioning anchor
 * + hardware cluster) on the right, the slackline spanning between the two
 * anchor master-points.
 */
(() => {
  if (customElements.get('bc-kit-scene')) return;

  const SVGNS = 'http://www.w3.org/2000/svg';

  // ---- world layout (SVG user units) -----------------------------------
  const W = 1600;
  const H = 700;
  const GROUND_Y = 560;          // top edge of the grass
  const CANOPY_CY = 165;         // canopy centre
  const TRUNK_TOP = 235;         // where the trunk meets the canopy
  const LINE_Y = 420;            // slackline attach height on the trunks
  const SAG = 0;                 // catenary sag of the main span (0 = straight)
  const DIM_Y = 500;             // max-rigable-length dimension line (below the span)

  // webbing rendering (matches the PDP diagram language in
  // snippets/bc-diagram-webbing-svg.liquid: two-tone split line + teardrop
  // sewn-loop ends + a sleeve overlay on each loop's outer half). WEB_T is both
  // the line thickness and the loop ring's total thickness, so they match.
  const WEB_T = 8;               // main webbing thickness (line + loop ring)
  const LOOP_S = 0.78;           // sewn-loop teardrop scale (PDP loop bbox 65x40)
  const LOOP_VSCALE = 0.8;       // extra vertical squash on the loop (shorter ovals)
  const SLEEVE_T = 14;           // sleeve overlay stroke width (bulges past the ring)
  const SLEEVE_T_STATIC = 11;    // thinner sleeve on the static-side (left) loop
  // signed offset from the loop throat to the main line's butt end (toward the
  // oval). Positive -> the line tucks slightly into the oval; negative -> a gap.
  const LINE_PUSH = 2;

  const LEFT_TREE_CX = 185;
  const RIGHT_TREE_CX = 1415;

  // The left/right master points (where each sling converges) are computed in
  // render() from the sling length + tree size; the webbing eyes, shackle and
  // tensioning cluster all follow. RIGHT_ANCHOR_X / RMASTER_X below are the
  // DEFAULT (≈30 cm tree, no-length) positions used to derive the cluster's
  // relative gaps.
  const RIGHT_ANCHOR_X = 1150;   // default right loop eye (meets the cluster)

  // right-side tensioning cluster, line-end -> tree (DEFAULT positions; render()
  // shifts the whole cluster when the right master point moves, preserving these
  // relative gaps).
  const MULT_X = 1080;           // multiplier sits inline on the line
  const CARAB_X = 1165;          // carabiner pass
  const ANCHOR_X = 1220;         // webbing-anchor device
  const RMASTER_X = 1278;        // right sling master-point + tri-load ring

  const RIGHT_EYE_GAP = RMASTER_X - RIGHT_ANCHOR_X;  // master -> loop eye (128)
  const MULT_GAP = RMASTER_X - MULT_X;               // master -> multiplier (198)
  const CARAB_GAP = RMASTER_X - CARAB_X;             // master -> carabiners (113)
  const ANCHOR_GAP = RMASTER_X - ANCHOR_X;           // master -> anchor (58)
  const LEFT_EYE_GAP = 40;                           // master -> loop eye (shackle span)

  // Sling sizing. To wrap a tree at all the sling must reach the full way
  // around it — at least the trunk circumference (π·diameter). The leftover
  // beyond that forms the V out to the master point; the forward length maps to
  // px below. If the sling is shorter than the circumference it can't reach
  // around (flagged "too short" + the triangle collapses to MIN_LEG_PX).
  const PX_PER_M = 145;          // metres -> px for the forward (V) length
  const MIN_LEG_PX = 24;
  const MAX_LEG_PX = 160;
  const DEFAULT_LEG_PX = 112;    // when no length is resolvable
  const SLING_W = 12;            // strap stroke width (thick — see Image #8)
  const SLING_SPREAD = 5;        // half the V opening at the master point
  const TP_FLAP = 7;             // tree-protection overhang past the trunk edge
  const TP_PX_PER_CM = 5.2;      // protector strip width (cm) -> band HEIGHT (px)
  const TP_H_MIN = 46;
  const TP_H_MAX = 150;

  // tree diameter (cm) -> trunk half-width (px)
  const DIA_MIN = 10;
  const DIA_MAX = 120;
  const HALF_MIN = 17;
  const HALF_MAX = 70;

  // viewBox zoom regions. The full view crops the canopy tops (y 70) and most of
  // the grass band (bottom 620) so the diagram reads shorter; zoom regions keep
  // the same ~2.9:1 aspect as the cropped full view to avoid letterbox.
  const ZOOM = {
    none: [0, 70, 1600, 550],
    left: [10, 190, 880, 303],
    right: [710, 190, 880, 303],
  };

  const DEFAULT_COLOR = '#8a8f96';
  const METAL_FILL = '#c4c9ce';
  const METAL_STROKE = '#8d949b';
  const SKY_ID = 'bcKitSky';

  // ---- small helpers ----------------------------------------------------

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  // Build an SVG node. attrs values of null/undefined are skipped; `text` sets
  // textContent; children may be nodes, arrays, or falsy (skipped).
  function el(tag, attrs, ...kids) {
    const n = document.createElementNS(SVGNS, tag);
    if (attrs) {
      for (const k in attrs) {
        const v = attrs[k];
        if (v == null) continue;
        if (k === 'text') n.textContent = v;
        else n.setAttribute(k, v);
      }
    }
    kids.flat().forEach((c) => { if (c) n.appendChild(c); });
    return n;
  }

  // Wrap children in a click-to-edit group bound to a slot role.
  function editGroup(role, ...kids) {
    return el('g', { 'data-bc-edit': role, class: 'bc-kit__edit' }, ...kids);
  }

  function trunkHalf(diameterCm) {
    const d = clamp(Number(diameterCm) || 30, DIA_MIN, DIA_MAX);
    const t = (d - DIA_MIN) / (DIA_MAX - DIA_MIN);
    return HALF_MIN + t * (HALF_MAX - HALF_MIN);
  }

  // Trunk half-width at an arbitrary height (trunk tapers top->base).
  function trunkHalfAtY(half, y) {
    const topHalf = half * 0.78;
    const t = clamp((y - TRUNK_TOP) / (GROUND_Y - TRUNK_TOP), 0, 1);
    return topHalf + (half - topHalf) * t;
  }

  // Trunk half-width at the slackline height.
  function trunkHalfAtLine(half) { return trunkHalfAtY(half, LINE_Y); }

  // Resolve an option's scene color, falling back to the webbing's top color
  // (girth-hitch slings reuse the webbing) then a neutral gray.
  function sceneColor(scene, webTopColor) {
    if (!scene) return DEFAULT_COLOR;
    if (scene.colorSource === 'webbing_diagram') return webTopColor || DEFAULT_COLOR;
    if (scene.color) return scene.color;
    return DEFAULT_COLOR;
  }

  // Resolve a slot's selected length in metres from its state. Custom length is
  // unit-aware; otherwise the variant option value is parsed (e.g. "2 m",
  // "1.5m", "150 cm", "6 ft"). Returns null when nothing numeric is present.
  function parseLengthM(ss, opt) {
    if (!ss) return null;
    if (ss.custom && opt && opt.custom) {
      const u = String(opt.custom.unit || 'm').toLowerCase();
      const v = Number(ss.length) || 0;
      if (!v) return null;
      return u === 'ft' ? v * 0.3048 : (u === 'cm' ? v / 100 : v);
    }
    const m = String(ss.variantValue || '').match(/([\d.]+)\s*(mm|cm|ft|in|m|")?/i);
    if (!m) return null;
    const v = parseFloat(m[1]);
    if (!(v > 0)) return null;
    const u = (m[2] || 'm').toLowerCase();
    if (u === 'cm') return v / 100;
    if (u === 'mm') return v / 1000;
    if (u === 'ft') return v * 0.3048;
    if (u === 'in' || u === '"') return v * 0.0254;
    return v;
  }

  // Forward (V) leg length in px for a sling of lengthM around a tree of diaCm:
  // leftover length after the trunk wrap (the full circumference, π·diameter),
  // halved, mapped to px. feasible=false when the sling is shorter than the
  // circumference — it can't reach all the way around the tree.
  function slingLeg(lengthM, diaCm) {
    if (!(lengthM > 0)) return { legPx: DEFAULT_LEG_PX, feasible: true };
    const circM = Math.PI * ((Number(diaCm) || 30) / 100);  // π·d = trunk circumference
    const forwardM = lengthM - circM;               // leftover after wrapping around
    const feasible = forwardM >= 0;                  // sling must reach the full way around
    const legPx = feasible ? clamp((forwardM / 2) * PX_PER_M, MIN_LEG_PX, MAX_LEG_PX) : MIN_LEG_PX;
    return { legPx, feasible };
  }

  // Effective tree-protector length: length_m × qty − overlap × (qty − 1).
  function treeProEffLen(scene, qty) {
    const lenM = Number(scene && scene.treeProLengthM) || 1.5;
    const overlapM = (Number(scene && scene.treeProOverlapCm) || 0) / 100;
    const q = Math.max(1, parseInt(qty, 10) || 1);
    return Math.max(lenM, lenM * q - overlapM * (q - 1));
  }

  // ---- component drawing ------------------------------------------------

  function drawTree(cx, half) {
    const topHalf = half * 0.78;
    const g = el('g', { class: 'bc-kit__tree' });
    g.appendChild(el('ellipse', { class: 'bc-kit__shadow', cx, cy: GROUND_Y + 10, rx: half + 64, ry: 15 }));
    g.appendChild(el('path', {
      class: 'bc-kit__trunk',
      d: `M ${cx - half} ${GROUND_Y} L ${cx - topHalf} ${TRUNK_TOP} L ${cx + topHalf} ${TRUNK_TOP} L ${cx + half} ${GROUND_Y} Z`,
    }));
    // canopy: three overlapping blobs
    g.appendChild(el('circle', { class: 'bc-kit__canopy', cx: cx - 78, cy: CANOPY_CY + 28, r: 96 }));
    g.appendChild(el('circle', { class: 'bc-kit__canopy', cx: cx + 78, cy: CANOPY_CY + 28, r: 96 }));
    g.appendChild(el('circle', { class: 'bc-kit__canopy', cx, cy: CANOPY_CY - 36, r: 116 }));
    return g;
  }

  // Tree protection, drawn in two layers so the anchor sling can sit between them
  // (pad UNDER the sling, vertical strap OVER it):
  //   - drawTreeProPad()   : the padded mat — wraps the trunk front. How far it
  //     reaches is the mat length vs the tree circumference (treeProCoverage):
  //     a too-short mat can't close around the back, so the band retracts toward
  //     the far (back-wrap) edge instead of spanning the whole trunk.
  //   - drawTreeProStrap() : the black mounting strap cinching it on, OVER the
  //     sling. Both layers' HEIGHT is the protector's physical strip width
  //     (constant per model); quantity only lengthens the wrap (see treeProCoverage).
  //
  // The band's height is the mat's physical strip width (tree_pro_width_cm) mapped
  // to px — a fixed value per protector. Joining more protectors makes the wrap
  // LONGER (more trunk circumference covered, via treeProCoverage), it does NOT
  // make the strip taller, so height is independent of qty / effective length.
  function treeProHeight(model) {
    const wCm = Number(model.scene && model.scene.treeProWidthCm) || 15;
    return clamp(wCm * TP_PX_PER_CM, TP_H_MIN, TP_H_MAX);
  }
  function treeProDrawable(model) {
    return model && model.scene && model.scene.shape === 'tree_pro_stripe';
  }

  // Fraction of the trunk the mat can wrap: effective mat length / tree
  // circumference, capped at one full wrap. <1 => it can't reach all the way
  // around, so the band shows only that share of the trunk.
  function treeProCoverage(model, diaCm) {
    const circM = Math.PI * ((Number(diaCm) || 30) / 100);   // π·d in metres
    if (!(circM > 0)) return 1;
    return clamp(treeProEffLen(model.scene, model.qty) / circM, 0, 1);
  }

  // Pad rect geometry. The band is anchored at the FAR (back-wrap) edge — farSign
  // +1 means that edge is to the right of the trunk centre, -1 to the left — so a
  // mat too short to wrap retracts toward the back instead of shrinking centred.
  function treeProGeom(cx, edge, farSign, model, diaCm) {
    const h = treeProHeight(model);
    const fullW = edge * 2 + TP_FLAP * 2;                 // full wrap: just past both edges
    const w = fullW * treeProCoverage(model, diaCm);
    const farX = cx + farSign * (edge + TP_FLAP);         // anchored back edge (keeps its flap)
    const x = Math.min(farX, farX - farSign * w);         // rect's left edge
    return { x, w, h, mid: x + w / 2 };
  }

  function drawTreeProPad(cx, edge, farSign, model, diaCm) {
    if (!treeProDrawable(model)) return null;
    const { x, w, h } = treeProGeom(cx, edge, farSign, model, diaCm);
    const g = editGroup(model.role);
    g.appendChild(el('rect', {
      class: 'bc-kit__tp-band', x, y: LINE_Y - h / 2, width: w, height: h, rx: 8,
      fill: sceneColor(model.scene, null),
    }));
    if (model.ghost) { g.setAttribute('opacity', '0.4'); g.setAttribute('stroke-dasharray', '7 5'); }
    return g;
  }

  function drawTreeProStrap(cx, edge, farSign, model, diaCm) {
    if (!treeProDrawable(model)) return null;
    const { mid, h } = treeProGeom(cx, edge, farSign, model, diaCm);
    const g = editGroup(model.role);
    g.appendChild(el('rect', {
      class: 'bc-kit__tp-strap', x: mid - SLING_W / 2, y: LINE_Y - h / 2, width: SLING_W, height: h,
    }));
    if (model.ghost) { g.setAttribute('opacity', '0.4'); g.setAttribute('stroke-dasharray', '7 5'); }
    return g;
  }

  // Anchor sling basket-hitched around the tree (see Image #8). Two strands run
  // from the master-point connector (a ring on the tensioning side; the static
  // side meets a shackle drawn later) around the trunk, and the sling reads as
  // wrapping because the strands straddle the tree in z: the UPPER strand is the
  // 'back' layer (emitted before the trunk + pad, so it ducks BEHIND them) and the
  // LOWER strand is the 'front' layer (emitted after the pad, so it crosses in
  // FRONT of it). drawSling() draws one layer per call, selected by `layer`.
  function slingGhost(model, n) {
    if (model.ghost) { n.setAttribute('opacity', '0.4'); n.setAttribute('stroke-dasharray', '7 5'); }
    return n;
  }

  function slingDrawable(model) {
    if (!(model && model.scene && model.scene.shape)) return false;
    const shape = model.scene.shape;
    // 'sling_girth' (the "None — girth-hitch the webbing" option) draws no basket
    // sling — the webbing wraps the trunk itself (see drawGirthWrap).
    return shape !== 'none' && shape !== 'sling_girth';
  }

  // The two strand paths of a tree wrap, shared by the anchor sling and the
  // girth-hitched webbing: a BACK pass (apex -> behind the trunk to the far edge)
  // and a FRONT pass (across the visible face -> curl up around the far edge),
  // both ending at the same far-edge point so the pair reads as one wrap. `apexX`
  // is where the strands converge — the sling master point, or (for a girth
  // hitch) the choke right at the trunk front; dir runs from the tree to the apex.
  function wrapStrandPaths(treeCx, apexX, padHalfW) {
    const dir = apexX > treeCx ? 1 : -1;
    const nearX = treeCx + dir * padHalfW;            // near (apex-side) pad edge
    const farX = treeCx - dir * padHalfW;             // far pad edge
    return {
      back:
        `M ${apexX} ${LINE_Y - SLING_SPREAD} ` +
        `C ${nearX} ${LINE_Y - 11} ${treeCx} ${LINE_Y - 13} ${farX + dir * 2} ${LINE_Y - 15}`,
      front:
        `M ${apexX} ${LINE_Y + SLING_SPREAD} ` +
        `C ${nearX} ${LINE_Y + 9} ${treeCx} ${LINE_Y + 8} ${farX} ${LINE_Y + 2} ` +
        `C ${farX - dir * 9} ${LINE_Y - 4} ${farX - dir * 10} ${LINE_Y - 15} ${farX + dir * 2} ${LINE_Y - 15}`,
    };
  }

  function drawSling(treeCx, masterX, padHalfW, model, drawRing, layer) {
    if (!slingDrawable(model)) return null;
    const color = model.color || DEFAULT_COLOR;
    const paths = wrapStrandPaths(treeCx, masterX, padHalfW);
    const g = editGroup(model.role);
    const stroke = (d) => slingGhost(model, el('path', {
      class: 'bc-kit__sling-strand', d, fill: 'none', stroke: color,
      'stroke-width': SLING_W, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    }));
    if (layer === 'back') {
      // upper strand (top of the V) — the BACK pass: master point sweeping across
      // to MEET the lower strand's far-edge wrap. Emitted before the trunk + pad
      // so it passes BEHIND them: visible from the master point until it reaches
      // the tree, then hidden behind the pad (the strand that wraps the back).
      g.appendChild(stroke(paths.back));
      return g;
    }
    // lower strand (bottom of the V) — the FRONT pass: master point -> across the
    // pad front with a slight sag -> a modest UP curl around the far edge. Emitted
    // after the pad so it stays IN FRONT of the tree (the strand that crosses the
    // visible front face), closing the loop at the same far-edge point as the back.
    g.appendChild(stroke(paths.front));
    // connector ring at the master point (tensioning side; static uses a shackle)
    if (drawRing) g.appendChild(el('circle', { class: 'bc-kit__ring', cx: masterX, cy: LINE_Y, r: 11, fill: 'none', 'stroke-width': 5 }));
    return g;
  }

  // Girth-hitch tunables, read fresh each render so the dev tuner
  // (tools/kit-scene-tuner.html) can drive them live via window.BC_KIT_GIRTH.
  // In production that global is absent, so these defaults apply unchanged.
  function girthCfg() {
    // Defaults dialed in via tools/kit-scene-tuner.html (2026-06-14).
    return Object.assign({
      bightScale: 0.48,    // bight loop size (teardrop scale)
      throatFrac: 0.81,    // fraction of the loop sitting ON the tree (rest hangs)
      spanStopDx: -7,      // px offset of the line's stop from the tree right edge
      bandRTilt: 5,        // band right-end height nudge (+down / -up)
      bandLTilt: -6,       // band left-end height nudge
      bandRDx: -22,        // band right-end horizontal nudge (the line around the tree)
      bandLDx: 0,          // band left-end horizontal nudge
      bandKink: -4,        // band mid bow / kink depth (+down)
      bightDy: 0,          // bight loop vertical nudge
      loopSleeve: true,    // sleeve overlay on the bight loop
      sleeveT: 7,          // sleeve thickness when loopSleeve is on
    }, (typeof window !== 'undefined' && window.BC_KIT_GIRTH) || {});
  }

  // Simplified girth hitch (matches the reference photo): the webbing wraps the
  // trunk as a two-tone band crossing the front face, and the standing line (the
  // main span, drawn by drawWebbing) passes THROUGH it — the front band is drawn
  // on top of the span so the line reads as tucked under the wrap. No sewn-loop
  // eye or overlap stripe: the wrap itself is the girth. Two parallel offset
  // strokes fake the two-tone (top above, lower below). `layer` splits front/back
  // so the trunk occludes the back pass like drawSling.
  //   back  : a shallow arc behind the trunk (a sliver shows over the top edge)
  //   front : a band crossing the visible face, angled down to the left
  // opts: { rTilt, lTilt, rDx, lDx, kink } — all additive, default 0, so the
  // band's shape is unchanged at neutral. rDx/lDx slide each end horizontally
  // (the line's wrap position); kink bows the middle of the front pass.
  function drawGirthBand(treeCx, padHalfW, top, lower, role, layer, opts) {
    opts = opts || {};
    const rTilt = opts.rTilt || 0, lTilt = opts.lTilt || 0, kink = opts.kink || 0;
    const r = treeCx + padHalfW + (opts.rDx || 0);   // front-right edge (standing side)
    const l = treeCx - padHalfW + (opts.lDx || 0);   // front-left edge
    const tilt = (x) => rTilt + (r - x) / (r - l) * (lTilt - rTilt);  // additive end-tilt
    const front = `M ${r} ${LINE_Y - 6 + rTilt} C ${treeCx + 6} ${LINE_Y - 1 + tilt(treeCx + 6) + kink} ${treeCx} ${LINE_Y + 4 + tilt(treeCx) + kink} ${treeCx - 4} ${LINE_Y + 6 + tilt(treeCx - 4) + kink} `
      + `C ${treeCx - 12} ${LINE_Y + 9 + tilt(treeCx - 12) + kink} ${l + 6} ${LINE_Y + 10 + tilt(l + 6)} ${l} ${LINE_Y + 11 + lTilt}`;
    const back = `M ${r} ${LINE_Y - 6 + rTilt} C ${treeCx + 8} ${LINE_Y - 14 + tilt(treeCx + 8)} ${l + 6} ${LINE_Y - 13 + tilt(l + 6)} ${l} ${LINE_Y + 11 + lTilt}`;
    const d = layer === 'back' ? back : front;
    const g = editGroup(role);
    if (lower) {
      g.appendChild(el('path', { d, fill: 'none', stroke: lower, 'stroke-width': WEB_T / 2, 'stroke-linecap': 'round', transform: `translate(0 ${WEB_T / 4})` }));
      g.appendChild(el('path', { d, fill: 'none', stroke: top, 'stroke-width': WEB_T / 2, 'stroke-linecap': 'round', transform: `translate(0 ${-WEB_T / 4})` }));
    } else {
      g.appendChild(el('path', { d, fill: 'none', stroke: top || DEFAULT_COLOR, 'stroke-width': WEB_T, 'stroke-linecap': 'round' }));
    }
    return g;
  }

  // Red "doesn't fit" badge: shown at a sling's apex when the selected length
  // can't reach around the chosen tree diameter. The label sits above the icon
  // with a white stroke halo (paint-order: stroke) so it reads over the scene.
  function drawWarning(x, y, label) {
    const g = el('g', { class: 'bc-kit__warn' });
    if (label) {
      g.appendChild(el('text', {
        class: 'bc-kit__warn-label', x, y: y - 22, text: label,
        'text-anchor': 'middle', fill: '#d23b2c',
        stroke: '#ffffff', 'stroke-width': 4, 'paint-order': 'stroke',
      }));
    }
    g.appendChild(el('circle', { cx: x, cy: y, r: 13, fill: '#d23b2c', stroke: '#ffffff', 'stroke-width': 2 }));
    g.appendChild(el('rect', { x: x - 1.6, y: y - 7, width: 3.2, height: 8.5, rx: 1.6, fill: '#ffffff' }));
    g.appendChild(el('circle', { cx: x, cy: y + 5, r: 1.8, fill: '#ffffff' }));
    return g;
  }

  // A small filled arrowhead with its tip at (x, y) pointing along `dir`
  // (-1 left, +1 right) — the terminator on each end of the dimension line.
  function dimArrow(x, y, dir) {
    const s = 11;
    return el('path', {
      class: 'bc-kit__dim-arrow',
      d: `M ${x} ${y} L ${x - dir * s} ${y - s * 0.5} L ${x - dir * s} ${y + s * 0.5} Z`,
    });
  }

  // Max-rigable-length dimension under the span: witness lines dropped from the
  // two trunk edges (at line height) to the dimension line, an arrowed line
  // between them, and a centred label ("Max rigable length" + value when set).
  // Styling is static (CSS classes) — no data-driven colors here.
  function drawMaxLength(leftX, rightX, label, value) {
    const g = el('g', { class: 'bc-kit__dim', 'aria-hidden': 'true' });
    g.appendChild(el('line', { class: 'bc-kit__dim-ext', x1: leftX, y1: LINE_Y, x2: leftX, y2: DIM_Y + 10 }));
    g.appendChild(el('line', { class: 'bc-kit__dim-ext', x1: rightX, y1: LINE_Y, x2: rightX, y2: DIM_Y + 10 }));
    g.appendChild(el('line', { class: 'bc-kit__dim-line', x1: leftX, y1: DIM_Y, x2: rightX, y2: DIM_Y }));
    g.appendChild(dimArrow(leftX, DIM_Y, -1));
    g.appendChild(dimArrow(rightX, DIM_Y, 1));
    g.appendChild(el('text', {
      class: 'bc-kit__dim-label',
      x: (leftX + rightX) / 2, y: DIM_Y - 12, 'text-anchor': 'middle',
      text: value ? `${label}: ${value}` : label,
    }));
    return g;
  }

  // A compact metallic bow shackle joining the static sling to the webbing's
  // sewn loop. The pin bulb seats in the loop eye (eyeX); the bow stretches
  // toward the sling apex (apexX). Emitted AFTER the webbing loop but BEFORE the
  // sling's front strand, so the stack reads front-to-back as sling -> shackle ->
  // loop (the bow sits on the loop eye; the sling apex clips over the bow).
  function drawShackle(eyeX, apexX) {
    const g = el('g', { class: 'bc-kit__shackle' });
    const bowCx = (eyeX + apexX) / 2;
    const bowRx = Math.abs(eyeX - apexX) / 2 + 4;
    // bow loop (the body the sling clips into)
    g.appendChild(el('ellipse', { cx: bowCx, cy: LINE_Y, rx: bowRx, ry: 8, fill: 'none', stroke: METAL_STROKE, 'stroke-width': 6 }));
    g.appendChild(el('path', {
      d: `M ${bowCx - bowRx + 5} ${LINE_Y - 3} Q ${bowCx} ${LINE_Y - 8} ${bowCx + bowRx - 5} ${LINE_Y - 3}`,
      fill: 'none', stroke: '#eef1f3', 'stroke-width': 1.8, 'stroke-linecap': 'round', opacity: 0.7,
    }));
    // pin bulb seated at the loop eye (covers the eye tip, leaving the rest of
    // the sleeve visible past it)
    g.appendChild(el('ellipse', { cx: eyeX, cy: LINE_Y, rx: 8, ry: 10, fill: METAL_FILL, stroke: METAL_STROKE, 'stroke-width': 2 }));
    g.appendChild(el('ellipse', { cx: eyeX - 2.5, cy: LINE_Y - 3, rx: 3, ry: 4, fill: '#ffffff', opacity: 0.5 }));
    return g;
  }

  // A sewn-loop teardrop "eye", traced directly from the PDP diagram loop
  // (snippets/bc-diagram-webbing-svg.liquid, left loop `M 80 92 C 70 72 ...`,
  // re-expressed tip-relative). `o` is the outward distance from the tip (0 =
  // throat where the line attaches, 65 = far rounded end); dir flips the bulge.
  function teardrop(tipX, tipY, dir, s) {
    const X = (o) => tipX + dir * o * s;
    const Y = (o) => tipY + o * s * LOOP_VSCALE;
    return `M ${X(0)} ${Y(0)} `
      + `C ${X(10)} ${Y(-20)} ${X(30)} ${Y(-20)} ${X(45)} ${Y(-15)} `
      + `C ${X(65)} ${Y(-11)} ${X(65)} ${Y(11)} ${X(45)} ${Y(15)} `
      + `C ${X(30)} ${Y(20)} ${X(10)} ${Y(20)} ${X(0)} ${Y(0)} Z`;
  }

  // The outer HALF of a loop — from the widest point (outward ≈28), around the
  // rounded far end, back to the widest point on the other side. The sleeve
  // overlay sits here, covering roughly the far half of the eye like the PDP
  // diagram (a thick band that bulges past the loop ring).
  function loopOuterArc(tipX, tipY, dir, s) {
    const X = (o) => tipX + dir * o * s;
    const Y = (o) => tipY + o * s * LOOP_VSCALE;
    return `M ${X(34)} ${Y(-18)} C ${X(48)} ${Y(-15)} ${X(65)} ${Y(-9)} ${X(65)} ${Y(0)} `
      + `C ${X(65)} ${Y(9)} ${X(48)} ${Y(15)} ${X(34)} ${Y(18)}`;
  }

  // One sewn-loop end, mirroring the PDP two-tone construction: the teardrop is
  // stroked twice — an outer ring (top color) scaled outward and an inner ring
  // (lower color) scaled inward around the loop centre, so together they read as
  // one WEB_T-thick loop split radially (outer half top, inner half lower).
  // Z-order: outer ring, inner ring, then the sleeve LAST (on top) — so the far
  // (sleeved) half reads as solid sleeve color and the inner (lower-color) edge
  // only shows on the near, un-sleeved half, like the webbing diagram.
  // No non-scaling-stroke: the ring strokes must scale with the viewBox just like
  // the main line, so the loop ring and the line read at the same thickness (the
  // offset scale transforms are ~1.1x, a negligible width shift).
  function drawLoop(tipX, tipY, dir, m, sleeveT, sOverride) {
    const s = sOverride || LOOP_S;
    const d = teardrop(tipX, tipY, dir, s);
    const cx = tipX + dir * 32.5 * s;     // teardrop geometric centre
    const cy = tipY;
    const g = el('g');
    const twoTone = m.twoTone && m.lowerColor;
    const ring = (stroke, sx, sy) => el('path', {
      d, fill: 'none', stroke, 'stroke-width': WEB_T / 2,
      'stroke-linejoin': 'round',
      transform: `translate(${cx} ${cy}) scale(${sx} ${sy}) translate(${-cx} ${-cy})`,
    });
    const vy = 80 * s * LOOP_VSCALE;     // y-offset denom tracks the squashed height
    if (twoTone) {
      g.appendChild(ring(m.topColor, 1 + WEB_T / (130 * s), 1 + WEB_T / vy));   // outer ring
      g.appendChild(ring(m.lowerColor, 1 - WEB_T / (130 * s), 1 - WEB_T / vy)); // inner ring
    } else {
      g.appendChild(el('path', { d, fill: 'none', stroke: m.topColor, 'stroke-width': WEB_T, 'stroke-linejoin': 'round' }));
    }
    if (m.sleeved && m.sleeveColor) {
      g.appendChild(el('path', {
        d: loopOuterArc(tipX, tipY, dir, s),
        fill: 'none', stroke: m.sleeveColor, 'stroke-width': sleeveT || SLEEVE_T, 'stroke-linecap': 'round',
      }));
    }
    return g;
  }

  // The doubled-back tail of the sewn loop: a short stripe just below the main
  // line with the two colors FLIPPED (the underside of the folded webbing), per
  // the PDP overlap stripe. dir points outward (toward the loop's far end); the
  // stripe runs inward from the throat, toward the centre of the line.
  function drawOverlap(throatX, dir, upperColor, lowerColor) {
    const s = LOOP_S;
    // oval-side end lines up with the main line's butt end (same LINE_PUSH
    // offset); the stripe runs back toward the line centre.
    const x1 = throatX + dir * LINE_PUSH;
    const x2 = x1 - dir * 33 * s;
    const oy = LINE_Y + WEB_T * 0.75;        // tucked just under the line
    const g = el('g');
    g.appendChild(el('line', { x1, y1: oy - WEB_T / 4, x2, y2: oy - WEB_T / 4, stroke: lowerColor, 'stroke-width': WEB_T / 2, 'stroke-linecap': 'butt' }));
    g.appendChild(el('line', { x1, y1: oy + WEB_T / 4, x2, y2: oy + WEB_T / 4, stroke: upperColor, 'stroke-width': WEB_T / 2, 'stroke-linecap': 'butt' }));
    return g;
  }

  // The slackline as a webbing: sewn-loop eyes at each end + a gently-sagging
  // two-tone span. Ordered like the PDP — loops first (behind), then the line on
  // top (its butt ends pushed a touch into each throat so the colors blend), then
  // the doubled-back overlap stripe. color-on-top already set m.topColor.
  // opts.skipLeftLoop: don't draw the left sewn loop / left overlap stripe, and
  // run the span's left butt straight to opts.leftSpanX (the trunk-front choke).
  // Used by the girth hitch, where the left loop is drawn separately (flipped to
  // face the span) and the span threads through it on its way to the trunk.
  function drawWebbing(m, leftEyeX, rightEyeX, opts) {
    opts = opts || {};
    const s = LOOP_S;
    const leftThroat = leftEyeX + 65 * s;    // line starts inboard of the eye
    const rightThroat = rightEyeX - 65 * s;
    const midX = (leftThroat + rightThroat) / 2;
    const top = m.topColor || DEFAULT_COLOR;
    const lower = m.lowerColor;
    const twoTone = !!(m.twoTone && lower);
    const g = editGroup('webbing');

    // (1) loops behind the line, hung from the line's bottom edge so the oval
    // centre sits at the main line's lower edge (= top of the overlap stripe)
    const loopCy = LINE_Y + WEB_T / 2;
    if (!opts.skipLeftLoop) g.appendChild(drawLoop(leftThroat, loopCy, -1, m, SLEEVE_T_STATIC));
    g.appendChild(drawLoop(rightThroat, loopCy, 1, m));

    // (2) main span on top; butt ends offset from the throats (LINE_PUSH < 0
    // leaves a small gap so the ovals sit clear of the line)
    const push = LINE_PUSH;
    const x1 = opts.leftSpanX != null ? opts.leftSpanX : leftThroat - push;
    const x2 = rightThroat + push;
    const band = (yy, w, color) => el('path', {
      d: `M ${x1} ${yy} Q ${midX} ${LINE_Y + SAG + (yy - LINE_Y)} ${x2} ${yy}`,
      fill: 'none', stroke: color, 'stroke-width': w, 'stroke-linecap': 'butt',
    });
    if (twoTone) {
      g.appendChild(band(LINE_Y - WEB_T / 4, WEB_T / 2, top));
      g.appendChild(band(LINE_Y + WEB_T / 4, WEB_T / 2, lower));
    } else {
      g.appendChild(band(LINE_Y, WEB_T, top));
    }

    // (3) doubled-back overlap stripe (inverted colors) near each loop
    if (twoTone) {
      if (!opts.skipLeftLoop) g.appendChild(drawOverlap(leftThroat, -1, top, lower));
      g.appendChild(drawOverlap(rightThroat, 1, top, lower));
    }
    return g;
  }

  // Short tensioning strand from the line end through the cluster to the right
  // master-point — the webbing continuing into the anchor (same top color).
  // Color is data-driven, so it is set as an attribute (no CSS class — a CSS
  // rule would override the presentation attribute).
  function drawTensionStrand(topColor, fromX, toX) {
    return el('line', {
      x1: fromX, y1: LINE_Y, x2: toX, y2: LINE_Y,
      stroke: topColor || DEFAULT_COLOR, 'stroke-width': 5, 'stroke-linecap': 'round',
    });
  }

  function carabiner(cx) {
    const g = el('g', { class: 'bc-kit__carab' });
    g.appendChild(el('rect', {
      x: cx - 12, y: LINE_Y - 24, width: 24, height: 48, rx: 12,
      fill: 'none', stroke: METAL_STROKE, 'stroke-width': 6,
    }));
    g.appendChild(el('line', { x1: cx + 8, y1: LINE_Y - 17, x2: cx + 8, y2: LINE_Y + 17, stroke: METAL_STROKE, 'stroke-width': 3 }));
    return g;
  }

  function drawCarabiners(role, count, baseX) {
    const n = Math.max(1, count || 2);
    const g = editGroup(role);
    for (let i = 0; i < n; i += 1) g.appendChild(carabiner(baseX + i * 18));
    return g;
  }

  // Tensioning webbing anchor: a square plate (MightyLock) or an oval ring
  // (Line-Locker). Color comes from the option scene (else metallic via CSS).
  function drawAnchor(role, shape, color, x) {
    const g = editGroup(role);
    if (shape === 'anchor_oval') {
      g.appendChild(el('ellipse', {
        cx: x, cy: LINE_Y, rx: 17, ry: 27, fill: 'none',
        stroke: color || METAL_STROKE, 'stroke-width': 8,
      }));
    } else {
      // anchor_square (default)
      g.appendChild(el('rect', { x: x - 20, y: LINE_Y - 23, width: 40, height: 46, rx: 6, fill: color || METAL_FILL, stroke: METAL_STROKE, 'stroke-width': 2 }));
      g.appendChild(el('rect', { x: x - 9, y: LINE_Y - 13, width: 18, height: 26, rx: 3, fill: '#ffffff', opacity: 0.5 }));
    }
    return g;
  }

  function pulley(cx, cy) {
    const g = el('g', { class: 'bc-kit__pulley' });
    g.appendChild(el('circle', { cx, cy, r: 16, fill: METAL_FILL, stroke: METAL_STROKE, 'stroke-width': 2 }));
    g.appendChild(el('circle', { cx, cy, r: 6, fill: '#ffffff', opacity: 0.55 }));
    return g;
  }

  // Multiplier (pulley system). pull_squared adds a stage for more advantage.
  function drawMultiplier(role, shape, x) {
    if (!shape || shape === 'none') return null;
    const g = editGroup(role);
    g.appendChild(el('line', { x1: x - 70, y1: LINE_Y, x2: x + 18, y2: LINE_Y, stroke: METAL_STROKE, 'stroke-width': 4, 'stroke-linecap': 'round' }));
    g.appendChild(pulley(x, LINE_Y - 22));
    g.appendChild(pulley(x - 36, LINE_Y + 22));
    if (shape === 'multiplier_pull_squared') {
      g.appendChild(pulley(x - 72, LINE_Y - 22));
    }
    return g;
  }

  // Tri-Load Prevention: a black ring at the master-point + a small reroute.
  function drawTriLoad(role, x) {
    const g = editGroup(role);
    g.appendChild(el('path', {
      class: 'bc-kit__triload-reroute', d: `M ${x - 22} ${LINE_Y} L ${x} ${LINE_Y - 14} L ${x + 22} ${LINE_Y}`,
      fill: 'none', 'stroke-width': 4,
    }));
    g.appendChild(el('circle', { class: 'bc-kit__triload-ring', cx: x, cy: LINE_Y, r: 20, fill: 'none', 'stroke-width': 7 }));
    return g;
  }

  // ---- element ----------------------------------------------------------

  class BcKitScene extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('svg');
      this._takeOver();
    }

    disconnectedCallback() {
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    }

    // Clear the pre-JS fallback and build backdrop + the dynamic layer once.
    _takeOver() {
      if (!this.svg || this._dyn) return;
      this.svg.replaceChildren();
      this.svg.setAttribute('viewBox', ZOOM.none.join(' '));

      const defs = el('defs', null,
        el('linearGradient', { id: SKY_ID, x1: 0, y1: 0, x2: 0, y2: 1 },
          el('stop', { offset: '0%', 'stop-color': '#cfe8f7' }),
          el('stop', { offset: '100%', 'stop-color': '#eef6ec' })));
      this.svg.appendChild(defs);
      this.svg.appendChild(el('rect', { class: 'bc-kit__scene-sky', x: 0, y: 0, width: W, height: H, fill: `url(#${SKY_ID})` }));
      this.svg.appendChild(el('rect', { class: 'bc-kit__scene-grass', x: 0, y: GROUND_Y, width: W, height: H - GROUND_Y }));
      this._dyn = el('g', { class: 'bc-kit__scene-dynamic' });
      this.svg.appendChild(this._dyn);
    }

    // Full redraw from the builder's central state. `meta` (optional) carries
    // annotations the scene can't derive itself — currently the max-rigable-
    // length dimension: { label, value } (value '' until the builder has one).
    render(state, options, meta) {
      this._takeOver();
      if (!this._dyn || !state || !options) return;
      const slots = state.slots || {};

      // resolve webbing colors (drives the line + girth slings)
      const web = slots.webbing || {};
      const webScene = (web.optionHandle && options[web.optionHandle] && options[web.optionHandle].scene) || null;
      let topColor = DEFAULT_COLOR;
      let lowerColor = null;
      let twoTone = false;
      if (webScene) {
        if (webScene.twoTone) {
          twoTone = true;
          const up = webScene.color || DEFAULT_COLOR;
          const lo = webScene.colorLower || up;
          topColor = web.colorOnTop === 'lower' ? lo : up;
          lowerColor = web.colorOnTop === 'lower' ? up : lo;
        } else {
          topColor = webScene.color || DEFAULT_COLOR;
        }
      }

      // each tree scales by its OWN diameter control (left lives in the static-
      // anchor step, right in the tensioning-anchor step); each only affects its
      // side's trunk + slings + tree-protection.
      const leftDia = (slots.left_tree_diameter && slots.left_tree_diameter.numeric) || 30;
      const rightDia = (slots.right_tree_diameter && slots.right_tree_diameter.numeric) || 30;
      const leftHalf = trunkHalf(leftDia);
      const rightHalf = trunkHalf(rightDia);
      const leftEdge = trunkHalfAtLine(leftHalf);
      const rightEdge = trunkHalfAtLine(rightHalf);

      const frag = document.createDocumentFragment();

      // slings wrap AROUND each tree (girth slings reuse the webbing color). The
      // static (left) end connects to a shackle at the webbing's sewn loop; the
      // tensioning (right) end connects to the hardware (ring connector).
      const ls = this._slotModel(slots, options, 'left_sling', topColor);
      const rs = this._slotModel(slots, options, 'right_sling', topColor);

      // "None (girth-hitch the webbing)": the static-sling option whose scene
      // shape is 'sling_girth' (kbo-sling-none). No basket sling, no shackle —
      // the webbing girth-hitches the trunk itself, loop choked at the trunk.
      const leftGirth = !!(ls && ls.scene && ls.scene.shape === 'sling_girth');
      const gc = girthCfg();   // girth tunables (overridable by the dev tuner)
      const bandOpts = { rTilt: gc.bandRTilt, lTilt: gc.bandLTilt, rDx: gc.bandRDx, lDx: gc.bandLDx, kink: gc.bandKink };

      // The selected sling length + tree size set the V size and the master-point
      // position: a longer sling (or smaller tree) pushes the master point out
      // from the tree (and the slackline shortens to match); too short and the
      // sling can't reach around — flagged, with the triangle collapsed.
      const leftLeg = slingLeg(ls && ls.lengthM, leftDia);
      const rightLeg = slingLeg(rs && rs.lengthM, rightDia);
      const leftMasterX = (LEFT_TREE_CX + leftEdge) + leftLeg.legPx;
      const rightMasterX = (RIGHT_TREE_CX - rightEdge) - rightLeg.legPx;
      if (ls && !leftLeg.feasible) ls.color = '#d23b2c';
      if (rs && !rightLeg.feasible) rs.color = '#d23b2c';

      // tree-protection pads (their half-width seats the sling at the pad edge)
      const ltp = this._slotModel(slots, options, 'left_tree_protection');
      const rtp = this._slotModel(slots, options, 'right_tree_protection');
      const leftPadHalf = leftEdge + (ltp ? TP_FLAP : 4);
      const rightPadHalf = rightEdge + (rtp ? TP_FLAP : 4);

      // On a girth hitch, when the tree is small enough that the left pad wraps
      // fully (reaches its near/right edge), render that pad OVER the main line so
      // the standing line tucks behind the protection at the trunk instead of
      // crossing in front of it. (Non-girth: the line never reaches this tree, so
      // it's scoped to girth to avoid covering the normal sling's front strand.)
      const leftPadOverLine = leftGirth && treeProDrawable(ltp) && treeProCoverage(ltp, leftDia) >= 1;

      // anchor positions derived from the master points (eyes + cluster follow).
      // Girth-hitch ("None") static sling: the webbing's own sewn loop chokes at
      // the trunk front (near pad edge) instead of riding a shackle out at the
      // master point, so the left eye snaps in to the trunk.
      const leftEyeX = leftMasterX + LEFT_EYE_GAP;        // left loop far end (eye)
      const rightEyeX = rightMasterX - RIGHT_EYE_GAP;     // right loop far end (eye)
      const multX = rightMasterX - MULT_GAP;
      const carabX = rightMasterX - CARAB_GAP;
      const anchorX = rightMasterX - ANCHOR_GAP;

      // The loops hang WEB_T/2 below the line; the sling, shackle and the
      // hardware that clips to the eyes follow them down by the same amount.
      const drop = (n) => { if (n) n.setAttribute('transform', `translate(0 ${WEB_T / 2})`); return n; };

      // (1) the sling's BACK strand (top of the V) FIRST, so the trunk + pad drawn
      // after it occlude the part that wraps around the back: it stays visible from
      // the master point until it reaches the tree, then ducks behind the pad.
      this._add(frag, drop(drawSling(LEFT_TREE_CX, leftMasterX, leftPadHalf, ls, false, 'back')));
      // girth wrap is tagged 'webbing' (not 'left_sling') so click/hover selects
      // the whole slackline — for a girth hitch the wrap IS the webbing's own end.
      if (leftGirth) this._add(frag, drawGirthBand(LEFT_TREE_CX, leftPadHalf, topColor, lowerColor, 'webbing', 'back', bandOpts));
      this._add(frag, drop(drawSling(RIGHT_TREE_CX, rightMasterX, rightPadHalf, rs, true, 'back')));

      // (2) trees (each edits its OWN diameter step); the trunk hides the back strand
      frag.appendChild(editGroup('left_tree_diameter', drawTree(LEFT_TREE_CX, leftHalf)));
      frag.appendChild(editGroup('right_tree_diameter', drawTree(RIGHT_TREE_CX, rightHalf)));

      // (3) tree-protection PAD on the visible front of the trunk. The mat is
      // anchored at the back-wrap edge — left tree wraps around its LEFT edge (-1),
      // right tree around its RIGHT edge (+1) — so a too-short mat retracts back.
      if (!leftPadOverLine) this._add(frag, drawTreeProPad(LEFT_TREE_CX, leftEdge, -1, ltp, leftDia));
      this._add(frag, drawTreeProPad(RIGHT_TREE_CX, rightEdge, 1, rtp, rightDia));

      // (4) the sling's FRONT strand (bottom of the V), over the pad: it crosses
      // the visible front face and curls around the far edge — so the bottom strand
      // reads in front of the protector while the top strand passes behind it. The
      // LEFT (static) front strand + its strap are deferred to after the webbing +
      // shackle (below) so the strand reads in FRONT of the shackle; only the RIGHT
      // strand + strap draw here.
      this._add(frag, drop(drawSling(RIGHT_TREE_CX, rightMasterX, rightPadHalf, rs, true, 'front')));

      // (5) the pad's vertical mounting strap (rides the retracted pad's centre,
      // so it stays on the mat when the band pulls toward the back)
      this._add(frag, drawTreeProStrap(RIGHT_TREE_CX, rightEdge, 1, rtp, rightDia));

      // tensioning strand from the right loop eye out to the right master point
      frag.appendChild(drop(drawTensionStrand(topColor, rightEyeX, rightMasterX)));

      // slackline as a webbing: sewn-loop ends + bi-color span. Ends are pinned
      // to "Sleeved" by the kit, so both loops carry the sleeve overlay. Girth:
      // the left loop is suppressed and the span runs onto the trunk front, where
      // the girth band (drawn next, on top) crosses over it — the line reads as
      // tucked through the wrap.
      const webM = {
        topColor, lowerColor, twoTone,
        sleeveColor: webScene && webScene.sleeveColor,
        sleeved: true,
      };
      // girth: the standing line stops at the tree's right edge (it threads the
      // hanging part of the bight there rather than running across the trunk).
      const treeRightEdge = LEFT_TREE_CX + leftPadHalf;
      frag.appendChild(drawWebbing(webM, leftEyeX, rightEyeX,
        leftGirth ? { skipLeftLoop: true, leftSpanX: treeRightEdge + gc.spanStopDx } : null));

      // static anchor (left side), layered front-to-back as SLING -> SHACKLE ->
      // LOOP: the webbing's sewn loop is drawn just above (so it sits BEHIND), the
      // shackle clips onto its eye, the sling's FRONT strand crosses in front of
      // the shackle, and the pad's mounting strap rides over the strand. Girth-
      // hitched: no shackle and the basket-sling front strand is null — only the
      // strap draws (still before the girth band below, so the band stays on top).
      if (ls && !leftGirth) this._add(frag, drop(drawShackle(leftEyeX, leftMasterX)));
      this._add(frag, drop(drawSling(LEFT_TREE_CX, leftMasterX, leftPadHalf, ls, false, 'front')));
      // non-girth: the pad strap rides on top of the sling's front strand.
      if (!leftGirth) this._add(frag, drawTreeProStrap(LEFT_TREE_CX, leftEdge, -1, ltp, leftDia));

      // girth: stack the static side as tree -> protection -> webbing -> strap.
      // (1) a full-coverage pad rides OVER the standing line so the line tucks
      // behind the protection; (2) the webbing wrap — the FRONT band (crossing
      // over the span so the line reads as threaded through the girth) + the
      // bight loop where the band folds back (sits mostly ON the trunk with ~1/3
      // hanging past the right edge, threading the line); (3) the pad's mounting
      // strap LAST, so the black strip sits on top of the webbing wrap. The wrap
      // is tagged 'webbing' (part of the slackline) for click/hover selection.
      if (leftGirth) {
        if (leftPadOverLine) this._add(frag, drawTreeProPad(LEFT_TREE_CX, leftEdge, -1, ltp, leftDia));
        this._add(frag, drawGirthBand(LEFT_TREE_CX, leftPadHalf, topColor, lowerColor, 'webbing', 'front', bandOpts));
        const bightM = { topColor, lowerColor, twoTone, sleeved: gc.loopSleeve, sleeveColor: webScene && webScene.sleeveColor };
        const bightThroatX = treeRightEdge - (65 * gc.bightScale) * gc.throatFrac;
        this._add(frag, editGroup('webbing', drawLoop(bightThroatX, LINE_Y + gc.bightDy, 1, bightM, gc.sleeveT, gc.bightScale)));
        this._add(frag, drawTreeProStrap(LEFT_TREE_CX, leftEdge, -1, ltp, leftDia));
      }

      // tensioning cluster (right side): multiplier -> carabiners -> anchor. The
      // multiplier sits inline on the main line (stays at LINE_Y); the carabiners
      // and anchor clip to the eye/ring, so they follow the loops down.
      const mult = this._slotModel(slots, options, 'multiplier');
      this._add(frag, drawMultiplier('multiplier', mult && mult.scene && mult.scene.shape, multX));

      const hw = this._slotModel(slots, options, 'tensioning_hardware');
      const carCount = hw && hw.scene && hw.scene.shape === 'carabiner_3' ? 3 : 2;
      this._add(frag, drop(drawCarabiners('tensioning_hardware', carCount, carabX)));

      const anchor = this._slotModel(slots, options, 'tensioning_webbing_anchor');
      if (anchor && anchor.scene && anchor.scene.shape && anchor.scene.shape !== 'none') {
        this._add(frag, drop(drawAnchor('tensioning_webbing_anchor', anchor.scene.shape, anchor.color, anchorX)));
      }

      // tri-load ring (toggle) — sits at the right master point, follows the drop
      if (slots.tri_load && slots.tri_load.on) {
        this._add(frag, drop(drawTriLoad('tri_load', rightMasterX)));
      }

      // infeasible-sling warnings (the sling can't reach around the chosen tree)
      if (ls && !leftLeg.feasible) this._add(frag, drawWarning(leftMasterX, LINE_Y - 36, 'Sling too short'));
      if (rs && !rightLeg.feasible) this._add(frag, drawWarning(rightMasterX, LINE_Y - 36, 'Sling too short'));

      // max-rigable-length dimension: trunk edge to trunk edge (at line height)
      const dimLabel = (meta && meta.label) || 'Max rigable length';
      this._add(frag, drawMaxLength(LEFT_TREE_CX + leftEdge, RIGHT_TREE_CX - rightEdge, dimLabel, meta && meta.value));

      this._dyn.replaceChildren(frag);
    }

    // Resolve a single-select slot to {role, scene, color, ghost, qty, lengthM}.
    _slotModel(slots, options, role, webTopColor) {
      const ss = slots[role];
      if (!ss || !ss.optionHandle) return null;
      const opt = options[ss.optionHandle];
      if (!opt) return null;
      return {
        role,
        scene: opt.scene || null,
        color: sceneColor(opt.scene, webTopColor),
        ghost: opt.kind === 'byo',
        qty: ss.quantity,
        lengthM: parseLengthM(ss, opt),
      };
    }

    _add(frag, node) { if (node) frag.appendChild(node); }

    // Animate the viewBox toward a tree (or back to the full scene).
    zoomTo(target) {
      const key = ZOOM[target] ? target : 'none';
      this.dataset.zoom = key;
      if (!this.svg) return;
      this._animateViewBox(ZOOM[key]);
    }

    _reduceMotion() {
      return typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _animateViewBox(to) {
      const svg = this.svg;
      if (!svg || !to) return;
      const cur = (svg.getAttribute('viewBox') || ZOOM.none.join(' ')).split(/[\s,]+/).map(Number);
      if (cur.length !== 4 || cur.some(Number.isNaN) || this._reduceMotion() || typeof performance === 'undefined') {
        svg.setAttribute('viewBox', to.join(' '));
        return;
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      const t0 = performance.now();
      const dur = 420;
      const ease = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
      const step = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const e = ease(p);
        const vb = cur.map((c, i) => c + (to[i] - c) * e);
        svg.setAttribute('viewBox', vb.join(' '));
        if (p < 1) this._raf = requestAnimationFrame(step);
        else this._raf = null;
      };
      this._raf = requestAnimationFrame(step);
    }

    // A self-contained, full-view SVG string for the printed build sheet.
    snapshotSVG() {
      if (!this.svg) return '';
      const clone = this.svg.cloneNode(true);
      clone.setAttribute('viewBox', ZOOM.none.join(' '));
      clone.removeAttribute('style');
      clone.removeAttribute('aria-hidden');
      // unique gradient id so the print copy never depends on the live scene
      return clone.outerHTML.split(SKY_ID).join(`${SKY_ID}Snap`);
    }
  }

  customElements.define('bc-kit-scene', BcKitScene);
})();
